import { ReactNode, useCallback, useMemo } from 'react';
import { T, useTranslate } from '@tolgee/react';
import TransComponent from 'components/TransComponent';
import { useAreaLabel, useTransportLabel } from 'views/BootGuide/views/AiModel/TransitDays';
import { useLabel as useSendPhotoBeforeShippingLabel } from 'views/BootGuide/views/AiModel/SendPhotoBeforeShipping';
import { useLabel as useSpecifyExpressTypeLabel } from 'views/BootGuide/views/AiModel/SpecifyExpressType';
import { useLabel as useCombinedShippingLabel } from 'views/BootGuide/views/AiModel/CombinedShipping';
import { useLabel as useFreeShippingLabel } from 'views/BootGuide/views/AiModel/FreeShipping';
import { useLabel as useProvideInvoiceLabel } from 'views/BootGuide/views/AiModel/ProvideInvoice';
import { useLabel as useDropsShippingLabel } from 'views/BootGuide/views/AiModel/DropsShipping';
import { useLabel as usePickUpLabel } from 'views/BootGuide/views/AiModel/PickUp';
import { useLabel as usePrivateShippingLabel } from 'views/BootGuide/views/AiModel/PrivateShipping';
import { useLabel as useGiftLabel } from 'views/BootGuide/views/AiModel/Gift';
import { useGetWorkingTimeText } from 'views/BootGuide/views/AiModel/ServiceWorkingTime';

export function useKnowledgeType(
  type: Knowledge.KnowledgeType,
  contentValue?: Knowledge.FormValue['contentValue'],
) {
  const { t } = useTranslate();
  const typeRenderer = useMemo(() => {
    switch (type) {
      case 'LOGISTICS_SEND_OUT_TIME':
        return t('knowledge.商品发货天数');
    }
  }, [t, type]);

  const typeValueRender = useMemo(() => {
    if (!contentValue) return '';
    return typeValue(t, type, contentValue.templateCode, contentValue.content);
  }, [t, type, contentValue]);

  return {
    typeRenderer,
    typeValueRender,
  };
}

export function typeValue(t, type, templateCode, content) {
  switch (type) {
    case 'LOGISTICS_SEND_OUT_TIME':
      try {
        const v = JSON.parse(content);
        switch (templateCode) {
          case 'UNDER_MAX':
            return t('knowledge.xDay以内string', {
              x: v?.max || '',
              day: t(`knowledge.${v?.unit}`),
            });
          case 'RANGE':
            return t('knowledge.商品发货天数rangeString', {
              x: v?.rangeMin || '',
              y: v?.rangeMax || '',
              day: t(`knowledge.${v?.unit}`),
            });
          case 'TODAY_OR_TOMORROW_ON_TIME':
            return t('knowledge.x 前当天发货 & y 后次日发货string', {
              x: v?.today_or_tomorrow_time || '',
              y: v?.today_or_tomorrow_time || '',
            });
          case 'AS_SOON':
            return t('knowledge.会尽快发货');
          default:
            return '';
        }
      } catch (e) {
        return '';
      }
    default:
      return '';
  }
}

export const useGetKnowledgeText = () => {
  const { t, getAreaLabel } = useAreaLabel();
  const { getTransportLabel } = useTransportLabel();
  const sendPhotoBeforeShippingLabel = useSendPhotoBeforeShippingLabel();
  const specifyExpressTypeLabel = useSpecifyExpressTypeLabel();
  const combinedShippingLabel = useCombinedShippingLabel();
  const freeShippingLabel = useFreeShippingLabel();
  const provideInvoiceLabel = useProvideInvoiceLabel();
  const dropsShippingLabel = useDropsShippingLabel();
  const pickUpLabel = usePickUpLabel();
  const privateShippingLabel = usePrivateShippingLabel();
  const giftLabel = useGiftLabel();
  const { getWorkingTimeText } = useGetWorkingTimeText();

  const knowledgeTextMap = useMemo(() => {
    const map: {
      [T in Knowledge.KnowledgeType]: (formValue: Knowledge.KnowledgeFormValueMap[T]) => ReactNode;
    } = {
      LOGISTICS_SEND_OUT_TIME: (formValue) => {
        const { valueType, range, underMax, todayOrTomorrowTime } = formValue;
        if (valueType === 'asSoon') {
          return t('会尽快发货');
        }
        if (valueType === 'time') {
          return (
            <T
              keyName="knowledge.x 前当天发货 & y 后次日发货"
              params={{
                x: <TransComponent content={<>{todayOrTomorrowTime}</>} />,
                y: <TransComponent content={<>{todayOrTomorrowTime}</>} />,
              }}
            />
          );
        }
        if (valueType === 'range') {
          return t('knowledge.商品发货天数rangeString', {
            x: range.rangeMin,
            y: range.rangeMax,
            day: {
              DAY: t('knowledge.DAY'),
              WORK_DAY: t('knowledge.WORK_DAY'),
              HOUR: t('knowledge.HOUR'),
            }[range.unit],
          });
        }
        if (valueType === 'underMax') {
          return (
            <T
              keyName="knowledge.xDay以内"
              params={{
                x: <TransComponent content={<>{underMax.max}</>} />,
                day: (
                  <TransComponent
                    content={
                      <>
                        {
                          {
                            DAY: t('knowledge.DAY'),
                            WORK_DAY: t('knowledge.WORK_DAY'),
                            HOUR: t('knowledge.HOUR'),
                          }[underMax.unit]
                        }
                      </>
                    }
                  />
                ),
              }}
            />
          );
        }
      },
      LOGISTICS_DELIVERED_IN_TIME: (formValue) => {
        const { valueType, range, areas, transports } = formValue;
        if (valueType === 'asSoon') {
          return t('会尽快运输');
        }
        if (valueType === 'range') {
          return (
            <T
              keyName="transit_days_range"
              params={{
                x: <TransComponent content={<>{range.min}</>} />,
                y: <TransComponent content={<>{range.max}</>} />,
              }}
            />
          );
        }
        if (valueType === 'area') {
          return areas.map(({ areas, rangeMin, rangeMax }, index) => {
            return (
              <>
                {areas.map(getAreaLabel).join(' ')}
                <T
                  keyName="transit_days_range"
                  params={{
                    x: <TransComponent content={<>{rangeMin}</>} />,
                    y: <TransComponent content={<>{rangeMax}</>} />,
                  }}
                />
                {index < areas.length - 1 && '，'}
              </>
            );
          });
        }
        if (valueType === 'transport') {
          return transports.map(({ deliveryMethods, rangeMin, rangeMax }, index) => {
            return (
              <>
                {deliveryMethods.map(getTransportLabel).join(' ')}
                <T
                  keyName="transit_days_range"
                  params={{
                    x: <TransComponent content={<>{rangeMin}</>} />,
                    y: <TransComponent content={<>{rangeMax}</>} />,
                  }}
                />
                {index < areas.length - 1 && '，'}
              </>
            );
          });
        }
      },
      LOGISTICS_SEND_PICTURES_BEFORE: (formValue) => {
        return sendPhotoBeforeShippingLabel[formValue.value];
      },
      LOGISTICS_DELIVERY_COURIER_TYPE: (formValue) => {
        return specifyExpressTypeLabel[formValue.value];
      },
      LOGISTICS_CONSOLIDATED_MULTIPLE_ORDER: (formValue) => {
        return combinedShippingLabel[formValue.value];
      },
      SHOP_FREE_SHIPPING: (formValue) => {
        return freeShippingLabel[formValue.freeShippingType];
      },
      SHOP_INVOICE: (formValue) => {
        return provideInvoiceLabel[formValue.value];
      },
      SHOP_DROPSHIPPING: (formValue) => {
        return dropsShippingLabel[formValue.value];
      },
      SHOP_PICKUP_AT_HOME: (formValue) => {
        return pickUpLabel[formValue.value];
      },
      SHOP_CUSTOMER_SERVICE_WORKING_TIME: (formValue) => {
        return getWorkingTimeText(formValue.weekRepeat);
      },
      ITEM_WARRANTY_SERVICE: (formValue) => {
        const { value, number, unit } = formValue;
        if (value === 'noSupport') {
          return t('本店商品无保修服务');
        }
        if (value === 'support') {
          return `${t('本店商品有保修服务')} ${t('保修期')} ${number} ${
            { YEAR: t('年'), MONTH: t('月') }[unit]
          }`;
        }
      },
      ITEM_EXPIRATION_DATE: (formValue) => {
        const { value, number, unit } = formValue;
        if (value === 'noSupport') {
          return t('参考详情页');
        }
        if (value === 'support') {
          return `${t('有保质期/有效期')} ${t('保质期/有效期')} ${number} ${
            { YEAR: t('年'), MONTH: t('月') }[unit]
          }`;
        }
      },
      OTHER_PRIVATE_SHIPPING: (formValue) => {
        return privateShippingLabel[formValue.value];
      },
      OTHER_GIFT: (formValue) => {
        return giftLabel[formValue.value];
      },
      SHOP_WHOLESALE: (formValue) => {
        return formValue.value === 'support' ? t('本店支持批发') : t('本店不支持批发');
      },
    };
    return map;
  }, [
    t,
    getAreaLabel,
    getTransportLabel,
    sendPhotoBeforeShippingLabel,
    specifyExpressTypeLabel,
    combinedShippingLabel,
    freeShippingLabel,
    provideInvoiceLabel,
    dropsShippingLabel,
    pickUpLabel,
    privateShippingLabel,
    giftLabel,
    getWorkingTimeText,
  ]);

  const getKnowledgeText = useCallback(
    <T extends Knowledge.KnowledgeType>(
      knowledgeType: T,
      formValues: Knowledge.KnowledgeFormValueMap[T],
    ) => {
      return knowledgeTextMap[knowledgeType](formValues);
    },
    [knowledgeTextMap],
  );

  return {
    t,
    knowledgeTextMap,
    getKnowledgeText,
  };
};
